import { Badge, Button, Loader, Paper, Text } from '@mantine/core';
import { IconZoomExclamation } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import type { GetSuiteTestRunOutputType, GetTestSuiteRunOutput } from '~/server/api/routers/test-suite-run-router';
import { AUTO_CONCURRENCY } from '~/server/logic/common/workers';
import { api } from '~/trpc-extra/api';
import type { VariableOverrides } from '~/types';
import { getVariableValue } from '~/server/logic/variable/value';

const ConfigItem = ({ label, value }: { label: string; value: React.ReactNode }) => {
  return !value ? null : (
      <div className="flex items-center justify-between border-b border-stone-100 py-3 last:border-0 dark:border-stone-800">
        <Text>{label}</Text>
        <Text>{value}</Text>
      </div>
    );
};

export default function RunDetailsView({
  projectId,
  suiteRun,
  isSuiteRunLoading,
  firstRegTestRun,
}: {
  projectId: string;
  suiteRun?: GetTestSuiteRunOutput;
  isSuiteRunLoading: boolean;
  firstRegTestRun?: GetSuiteTestRunOutputType;
}) {
  const { isAdmin } = useAuthData();

  const { data: environmentVariables, isLoading: isLoadingVariables } = api.variable.list.useQuery({
    projectId: projectId!,
    environments: [suiteRun?.environment ?? 'NONE'],
  });

  const { data: entitlements, isLoading: isLoadingEntitlements } = api.team.getEntitlements.useQuery();

  const usedVariables = useMemo((): VariableOverrides => {
    const envVars = Object.fromEntries(
      environmentVariables?.map((x) => [
        x.name,
        { sensitive: x.sensitive, value: x.sensitive ? '' : getVariableValue(x.value) },
      ]) ?? [],
    );

    const variableOverrides = (suiteRun?.overrideVariables ?? {}) satisfies VariableOverrides;
    return {
      ...envVars,
      ...variableOverrides,
    };
  }, [suiteRun?.overrideVariables, environmentVariables]);

  const hasSuiteSettings =
    firstRegTestRun?.maxRetries !== undefined ||
    firstRegTestRun?.repeatEach !== undefined ||
    firstRegTestRun?.operationDelayMs !== undefined ||
    firstRegTestRun?.testTimeoutMs !== undefined ||
    suiteRun?.numberOfWorkers !== undefined;

  const hasEnvironmentSettings = suiteRun?.environment !== undefined || Object.keys(usedVariables).length > 0;

  const hasUrlOverrides = !!firstRegTestRun?.domainOverrides && firstRegTestRun.domainOverrides.length > 0;

  return (
    <div className="mt-4 space-y-6">
      <Text
        size="lg"
        fw={600}
      >
        Run Configuration
      </Text>

      {isSuiteRunLoading ?
        <div className="space-y-3">
          <div className="h-6 w-48 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
          <div className="h-6 w-36 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
          <div className="h-6 w-52 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
        </div>
      : <div className="space-y-4">
          {hasSuiteSettings ?
            <Paper
              p="lg"
              radius="md"
              withBorder
              className="max-w-4xl"
            >
              <Text
                size="md"
                fw={600}
                className="mb-3"
              >
                Suite Settings
              </Text>
              <div>
                {firstRegTestRun?.maxRetries !== undefined && (
                  <ConfigItem
                    label="Max Retries"
                    value={firstRegTestRun.maxRetries}
                  />
                )}
                {firstRegTestRun?.repeatEach !== undefined && (
                  <ConfigItem
                    label="Repeat Each"
                    value={firstRegTestRun.repeatEach}
                  />
                )}
                {firstRegTestRun?.operationDelayMs !== undefined && (
                  <ConfigItem
                    label="Action Delay"
                    value={`${firstRegTestRun.operationDelayMs}ms`}
                  />
                )}
                {firstRegTestRun?.testTimeoutMs !== undefined && (
                  <ConfigItem
                    label="Test Timeout"
                    value={`${firstRegTestRun.testTimeoutMs}ms`}
                  />
                )}
                {suiteRun?.numberOfWorkers !== undefined && (
                  <ConfigItem
                    label="Number of Workers"
                    value={
                      suiteRun.numberOfWorkers === AUTO_CONCURRENCY ? 'Auto'
                      : entitlements?.maxConcurrency && suiteRun.numberOfWorkers === entitlements.maxConcurrency ?
                        <span className="flex items-center gap-2">
                          {suiteRun.numberOfWorkers}
                          <Badge
                            color="yellow"
                            size="xs"
                            variant="light"
                          >
                            plan limit
                          </Badge>
                        </span>
                      : suiteRun.numberOfWorkers
                    }
                  />
                )}
              </div>
            </Paper>
          : null}

          {hasEnvironmentSettings ?
            <Paper
              p="lg"
              radius="md"
              withBorder
              className="max-w-4xl"
            >
              <Text
                size="md"
                fw={600}
                className="mb-3"
              >
                Environment
              </Text>
              <div>
                {suiteRun?.environment !== undefined && (
                  <ConfigItem
                    label="Environment Name"
                    value={suiteRun.environment}
                  />
                )}
                {isLoadingVariables || isLoadingEntitlements ?
                  <div className="py-3">
                    <Loader size="sm" />
                  </div>
                : Object.keys(usedVariables).length > 0 && (
                    <div className="border-t border-stone-100 pt-3 dark:border-stone-800">
                      <Text
                        size="xs"
                        fw={600}
                        className="mb-2 text-stone-600 dark:text-stone-400"
                      >
                        Variables
                      </Text>
                      <div className="space-y-2">
                        {Object.entries(usedVariables).map(([key, val]) => (
                          <div
                            key={key}
                            className="flex items-center justify-between rounded-md bg-stone-50 px-3 py-2 dark:bg-stone-900"
                          >
                            <code className="text-xs font-medium text-stone-700 dark:text-stone-300">{key}</code>
                            <div className="text-xs text-stone-600 dark:text-stone-400">
                              {val.sensitive ?
                                <Badge
                                  variant="light"
                                  color="gray"
                                >
                                  Sensitive
                                </Badge>
                              : typeof val.value !== 'object' ?
                                <code>{val.value}</code>
                              : <details className="group">
                                  <summary className="cursor-pointer text-right text-xs text-stone-600 hover:underline dark:text-stone-400">
                                    JSON Object
                                  </summary>
                                  <pre className="mt-2 max-h-40 overflow-auto rounded bg-stone-100 p-2 text-xs text-stone-700 dark:bg-stone-800 dark:text-stone-200">
                                    {JSON.stringify(val.value, null, 2)}
                                  </pre>
                                </details>
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }
              </div>
            </Paper>
          : null}

          {!!firstRegTestRun?.domainOverrides ?
            <Paper
              p="lg"
              radius="md"
              withBorder
              className="max-w-4xl"
            >
              <div className="mb-3 flex items-center gap-2">
                <Text
                  size="md"
                  fw={600}
                >
                  URL Overrides
                </Text>
                <Badge
                  variant="light"
                  color="gray"
                  size="xs"
                >
                  Deprecated
                </Badge>
              </div>
              <div className="space-y-2">
                {firstRegTestRun?.domainOverrides.map(({ original, replacement }, idx) => (
                  <div
                    key={original ?? idx}
                    className="flex items-center gap-2 rounded-md bg-stone-50 px-3 py-2 dark:bg-stone-900"
                  >
                    <code className="text-xs text-stone-600 dark:text-stone-400">{original}</code>
                    <span className="text-stone-400">→</span>
                    <code className="text-xs text-stone-600 dark:text-stone-400">{replacement}</code>
                  </div>
                ))}
              </div>
            </Paper>
          : null}

          {!hasSuiteSettings && !hasEnvironmentSettings && !hasUrlOverrides ?
            <Text
              size="sm"
              c="dimmed"
              className="italic"
            >
              No configuration settings for this run
            </Text>
          : null}
        </div>
      }

      {!!suiteRun && !suiteRun.testSuite.temp && (
        <Button
          variant="light"
          className="mt-2"
          onClick={() => {
            if (suiteRun?.testSuite.id) {
              window.open(`/project/${projectId}/testSuite/${suiteRun.testSuite.id}`, '_blank');
            }
          }}
        >
          Manage Suite
        </Button>
      )}

      {!!isAdmin && !!suiteRun && (
        <div className="mt-8 border-t pt-6 dark:border-stone-700">
          <Text
            size="lg"
            fw={500}
            className="mb-4"
          >
            Admin Details
          </Text>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <IconZoomExclamation
                size={16}
                className="text-orange-500"
              />
              <div className="text-sm text-orange-500">ECS Task ID:</div>
              <div className="text-sm">
                {suiteRun?.executionContext ? JSON.stringify(suiteRun.executionContext, null, 2) : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
